import { Container, Rectangle, Sprite, Text } from 'pixi.js';
import { formatCurrencyDigits } from '../lib/formatCurrency.js';

/** Quick-bet / chip labels: two decimals below 100; from 100 up, no fraction. */
export function formatQuickBetAmount(n, precision = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  if (Number.isFinite(precision) && precision !== 2) {
    return formatCurrencyDigits(v, precision);
  }
  const abs = Math.abs(v);
  if (abs >= 100) {
    return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FORMATTED_AMOUNT_SMALL_FONT_FROM_DIGITS = 5;

function digitCountForAmountFont(formatted) {
  const compact = String(formatted ?? '').replace(/[\s,]/g, '');
  const withoutTrailingCents = /\.00$/.test(compact) ? compact.slice(0, -3) : compact;
  return withoutTrailingCents.replace(/\D/g, '').length;
}

/** Used by quick-bet chips and autoplay round labels when the string is numeric-ish. */
export function fontSizeWhenFormattedLong(formatted, normalPx, smallPx) {
  return digitCountForAmountFont(formatted) >= FORMATTED_AMOUNT_SMALL_FONT_FROM_DIGITS ? smallPx : normalPx;
}

/**
 * Fit quick-bet chip art in the cell with one scale factor (no non-uniform stretch); centers sprite.
 * @param {Sprite} sprite
 * @param {Texture} tex
 * @param {number} cellW
 * @param {number} cellH
 */
export function layoutQuickBetChipSpriteInCell(sprite, tex, cellW, cellH) {
  const nw = tex?.width > 0 ? tex.width : 100;
  const nh = tex?.height > 0 ? tex.height : 100;
  const k = Math.min(cellW / nw, cellH / nh);
  sprite.width = nw * k;
  sprite.height = nh * k;
  sprite.x = (cellW - sprite.width) * 0.5;
  sprite.y = (cellH - sprite.height) * 0.5;
}

/**
 * Single design rect: all children laid out here once. The whole group is uniformly scaled to fit `cellW`×`cellH` — no per-device text coordinates.
 */
const REF = { w: 100, h: 100 };

/** One structure only (logical px in REF space). */
const CHIP_DESIGN = {
  xBias: 1.5,
  fun: { y: 20, fontSize: 14 },
  valueY: 53,
  valueFontN: 24,
  valueFontS: 24,
  bottom: { y: 97, fontSize: 14 },
};

function chipTextFills(selected) {
  return {
    fun: selected ? 0xffea00 : 0xffffff,
    value: selected ? 0xd0ff00 : 0xffffff,
    bottom: 0xffea00,
  };
}

/**
 * Quick-bet chip: textured bg + FUN + amount + bottom label. One internal layout; only `cellW` / `cellH` change — everything scales together.
 *
 * @param {object} p
 * @param {number} p.betValue
 * @param {boolean} p.selected
 * @param {import('pixi.js').Texture} p.textureNormal
 * @param {import('pixi.js').Texture} p.textureSelected
 * @param {number} p.cellW
 * @param {number} p.cellH
 * @param {string} [p.bottomText='BET']
 * @param {string} [p.currencyLabel='FUN']
 * @param {number} [p.currencyPrecision=2]
 * @param {string} [p.fontFamily]
 */
export function createQuickBetChipPixi(p) {
  const fontFamily = p.fontFamily ?? '"Montserrat", "Arial", sans-serif';
  const D = CHIP_DESIGN;
  const bottomText = p.bottomText ?? 'BET';
  const currencyLabel = p.currencyLabel ?? 'FUN';
  const selected = !!p.selected;
  const tex = selected ? p.textureSelected : p.textureNormal;

  const outer = new Container();
  const inner = new Container();

  const bg = new Sprite(tex);
  bg.eventMode = 'none';
  layoutQuickBetChipSpriteInCell(bg, tex, REF.w, REF.h);

  const fills = chipTextFills(selected);
  const xmid = REF.w / 2 + D.xBias;
  const amtStr = formatQuickBetAmount(p.betValue, p.currencyPrecision ?? 2);
  const valueFontSize = fontSizeWhenFormattedLong(amtStr, D.valueFontN, D.valueFontS);

  const fun = new Text({
    text: currencyLabel,
    style: {
      fontFamily,
      fontSize: D.fun.fontSize,
      fontWeight: '600',
      fill: fills.fun,
    },
  });
  fun.anchor.set(0.5, 0);
  fun.position.set(xmid, D.fun.y);
  fun.eventMode = 'none';

  const amt = new Text({
    text: amtStr,
    style: {
      fontFamily,
      fontSize: valueFontSize,
      fontWeight: '700',
      fill: fills.value,
    },
  });
  amt.anchor.set(0.5, 0.5);
  amt.position.set(xmid, D.valueY);
  amt.eventMode = 'none';

  const bottom = new Text({
    text: bottomText,
    style: {
      fontFamily,
      fontSize: D.bottom.fontSize,
      fontWeight: '700',
      fill: fills.bottom,
    },
  });
  bottom.anchor.set(0.5, 1);
  bottom.position.set(xmid, D.bottom.y);
  bottom.eventMode = 'none';

  inner.addChild(bg, fun, amt, bottom);

  const scale = Math.min(p.cellW / REF.w, p.cellH / REF.h);
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  inner.scale.set(s);
  inner.position.set((p.cellW - REF.w * s) / 2, (p.cellH - REF.h * s) / 2);

  outer.addChild(inner);
  outer.eventMode = 'static';
  outer.cursor = 'pointer';
  outer.hitArea = new Rectangle(0, 0, p.cellW, p.cellH);

  return outer;
}
